import { Text } from '@mantine/core'
import type { UseBaseQueryResult } from '@tanstack/react-query'
import { castArray } from 'lodash-es'
import type { ReactElement, ReactNode } from 'react'
import { Loader } from '#src/components/core/Loader.tsx'

export type FhirQueryLoaderProps<TQueryType extends FhirQueryType> = {
  query: TQueryType
  loader?: ReactElement<any> | null
  error?: ((error: Error | null) => ReactElement<any>) | null
  children?:
    | ReactNode
    | ((
        data: GetDataTypeFromQueryResultOrQueryResultList<TQueryType>
      ) => ReactNode)
}

/**
 * `query` can either be the result of a `useFhirSearch[One]` query or an array of such queries.
 * Each can also be a configurable query object, which allows for nullable queries.
 *
 * @param props - Props for the FhirQueryLoader component.
 * @returns - UI component that loads data from a FHIR query and displays a loading spinner, error message, or the loaded data.
 */
export function FhirQueryLoader<const TQueryType extends FhirQueryType>(
  props: FhirQueryLoaderProps<TQueryType>
): ReactNode {
  const queryConfigs = getConfigurableQueriesFromProps(props.query)
  const isLoading = queryConfigs.some((cfg) => cfg.query.isLoading)
  const isError = queryConfigs.some((cfg) => cfg.query.isError) && !isLoading

  if (isLoading) {
    return props.loader ?? <Loader />
  }
  if (isError) {
    return (
      props.error?.(
        queryConfigs.find((cfg) => cfg.query.isError)?.query?.error ?? null
      ) ?? null
    )
  }

  if (typeof props.children === 'function') {
    if (
      queryConfigs.some(
        (cfg) => cfg.nullable === false && cfg.query.data == null
      )
    ) {
      // only return null if any non-nullable query is null
      return <Text c="red">We could not load the data for this component.</Text>
    }

    // if the query prop is a list, pass a list of data to the children function
    return Array.isArray(props.query)
      ? props.children(
          queryConfigs.map(
            (cfg) => cfg.query.data
          ) as GetDataTypeFromQueryResultOrQueryResultList<TQueryType>
        )
      : props.children(
          queryConfigs[0].query
            .data as GetDataTypeFromQueryResultOrQueryResultList<TQueryType>
        )
  } else {
    return props.children
  }
}

/**
 * Extracts the query and nullable properties from the query prop.
 * If the query prop is a list, each query is extracted individually.
 *
 * @param queryProp - The query prop from the FhirQueryLoader component.
 * @returns - A list of ConfigurableFhirQuery's constructed from the query prop.
 */
function getConfigurableQueriesFromProps<TQueryType extends FhirQueryType>(
  queryProp: TQueryType
): ConfigurableFhirQuery[] {
  return castArray(queryProp).map((query) =>
    'query' in query
      ? // if the query is a configurable query, extract the query and nullable properties
        { query: query.query, nullable: query.nullable ?? false }
      : // if the query is a base query, set nullable to false
        { query, nullable: false }
  )
}

/**
 * The given `query` can be a single query or a list of queries.
 */
type FhirQueryType = ConfigurableOrBaseQueryType | ConfigurableOrBaseQueryType[]

/**
 * Each query could be just a base query or a configurable query.
 */
type ConfigurableOrBaseQueryType = UseBaseQueryResult | ConfigurableFhirQuery

/**
 * A configurable FHIR query that allows the result to be nullable.
 * Future iterations of this type may include additional configuration options.
 */
export type ConfigurableFhirQuery<
  TQueryResult extends UseBaseQueryResult = UseBaseQueryResult,
  TNullable extends boolean = boolean,
> = {
  query: TQueryResult
  nullable?: TNullable
}

/**
 * Extracts the inner data type from a query or a list of queries.
 * If a single query is provided, returns the data type of that query.
 * If a list of queries is provided, returns a list of the data types of each query.
 */
type GetDataTypeFromQueryResultOrQueryResultList<
  TQueryType extends FhirQueryType,
> =
  // if we have a query list, recursively extract and return their data types
  TQueryType extends ConfigurableOrBaseQueryType[]
    ? GetDataTypeFromQueryResultListRecursively<TQueryType>
    : // if we have a single query, extract and return its data type
      TQueryType extends ConfigurableOrBaseQueryType
      ? GetDataTypeFromConfigurableOrBaseQueryType<TQueryType>
      : never

/**
 * Extracts the inner data types from a list of query and returns them
 * as a list of data types in the same order as the query types.
 */
type GetDataTypeFromQueryResultListRecursively<
  TQueryType extends ConfigurableOrBaseQueryType[],
> =
  // base case: empty list
  TQueryType extends []
    ? // return empty list
      []
    : // recursive case: non-empty list
      TQueryType extends [
          infer TFirst extends ConfigurableOrBaseQueryType,
          ...infer TRest extends ConfigurableOrBaseQueryType[],
        ]
      ? // return the data type of the first query, and recurse on the rest
        [
          GetDataTypeFromConfigurableOrBaseQueryType<TFirst>,
          ...GetDataTypeFromQueryResultListRecursively<TRest>,
        ]
      : []

/**
 * Extracts the inner data type from a configurable query or a base query.
 */
type GetDataTypeFromConfigurableOrBaseQueryType<
  TQueryType extends ConfigurableOrBaseQueryType,
> =
  TQueryType extends UseBaseQueryResult<infer TData>
    ? Exclude<TData, undefined | null>
    : TQueryType extends ConfigurableFhirQuery
      ? GetDataTypeFromConfigurableQuery<TQueryType>
      : never

/**
 * Extracts the inner data type from a configurable query.
 * This is where configuration options are handled, and where
 * additional configuration options need to be supported in the future.
 */
type GetDataTypeFromConfigurableQuery<
  TQueryType extends ConfigurableFhirQuery,
> = TQueryType extends { nullable?: true }
  ? GetDataTypeFromQueryResultOrQueryResultList<TQueryType['query']> | null
  : GetDataTypeFromQueryResultOrQueryResultList<TQueryType['query']>
